/**
 * @file     ChatChartCard.tsx
 * @purpose  In-chat data visualization: a visx area/line chart of a real
 *           market series (HPI, rents, rates) with a skeleton shimmer that
 *           cross-fades into the drawn chart. Aggregate board data only.
 */
'use client';

import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { scaleLinear, scalePoint } from '@visx/scale';
import { AreaClosed, LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { LinearGradient } from '@visx/gradient';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ChartWidget } from '@/services/ai-chat/capabilities/types';

const HEIGHT = 180;
const MARGIN = { top: 12, right: 12, bottom: 24, left: 12 };
const SKELETON_MS = 450;

function fmtValue(v: number, unit: string): string {
  if (unit === '$') {
    return v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(2)}M` : v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`;
  }
  return `${v}${unit}`;
}

function Chart({ widget, width }: { widget: ChartWidget; width: number }) {
  const { theme } = useAppTheme();
  const brand = theme.palette.brand.primary;
  const innerW = Math.max(10, width - MARGIN.left - MARGIN.right);
  const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

  const { xScale, yScale, first, last, min, max } = useMemo(() => {
    const xs = widget.points.map((p) => p.x);
    const ys = widget.points.map((p) => p.y);
    const lo = Math.min(...ys);
    const hi = Math.max(...ys);
    const pad = (hi - lo) * 0.1 || hi * 0.05 || 1;
    return {
      xScale: scalePoint<string>({ domain: xs, range: [0, innerW] }),
      yScale: scaleLinear<number>({ domain: [lo - pad, hi + pad], range: [innerH, 0] }),
      first: widget.points[0],
      last: widget.points[widget.points.length - 1],
      min: lo,
      max: hi,
    };
  }, [widget.points, innerW, innerH]);

  return (
    <svg width={width} height={HEIGHT} role="img" aria-label={widget.title}>
      <LinearGradient id="relta-chart-fill" from={brand} to={brand} fromOpacity={0.28} toOpacity={0.02} />
      <Group left={MARGIN.left} top={MARGIN.top}>
        <AreaClosed
          data={widget.points}
          x={(d) => xScale(d.x) ?? 0}
          y={(d) => yScale(d.y)}
          yScale={yScale}
          curve={curveMonotoneX}
          fill="url(#relta-chart-fill)"
        />
        <LinePath
          data={widget.points}
          x={(d) => xScale(d.x) ?? 0}
          y={(d) => yScale(d.y)}
          curve={curveMonotoneX}
          stroke={brand}
          strokeWidth={2}
        />
        <circle cx={xScale(last.x) ?? 0} cy={yScale(last.y)} r={3.5} fill={brand} />
        <text x={0} y={innerH + 16} fontSize={10} fill={theme.semanticColors.textMuted}>{first.x}</text>
        <text x={innerW} y={innerH + 16} fontSize={10} textAnchor="end" fill={theme.semanticColors.textMuted}>{last.x}</text>
        <text x={0} y={yScale(max) - 2} fontSize={10} fill={theme.semanticColors.textMuted}>{fmtValue(max, widget.unit)}</text>
        <text x={0} y={yScale(min) + 10} fontSize={10} fill={theme.semanticColors.textMuted}>{fmtValue(min, widget.unit)}</text>
      </Group>
    </svg>
  );
}

export function ChatChartCard({ widget }: { widget: ChartWidget }) {
  const { theme } = useAppTheme();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setReady(true), SKELETON_MS);
    return () => clearTimeout(id);
  }, []);

  const last = widget.points[widget.points.length - 1];

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 1,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        backgroundColor: theme.semanticColors.surfaceCard,
        overflow: 'hidden',
        maxWidth: 560,
        width: '100%',
      }}
    >
      <Box sx={{ px: 2, pt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: theme.semanticColors.textPrimary }}>
          {widget.title}
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: theme.palette.brand.primary, whiteSpace: 'nowrap' }}>
          {fmtValue(last.y, widget.unit)}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', height: HEIGHT }}>
        <Box sx={{ position: 'absolute', inset: 0, px: 2, py: 1, opacity: ready ? 0 : 1, transition: 'opacity 0.35s ease', pointerEvents: 'none' }}>
          <Skeleton variant="rounded" width="100%" height="100%" animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
        <Box sx={{ position: 'absolute', inset: 0, opacity: ready ? 1 : 0, transition: 'opacity 0.45s ease' }}>
          <ParentSize>{({ width }) => (width > 0 ? <Chart widget={widget} width={width} /> : null)}</ParentSize>
        </Box>
      </Box>
      <Typography sx={{ px: 2, pb: 1, fontSize: 10.5, color: theme.semanticColors.textMuted }}>
        {widget.source}
      </Typography>
    </Box>
  );
}
